// Seletores do tour guiado da Jornada no Compliance — a metade que o backend
// não pode ter.
//
// Mesmo contrato do piloto do Fee Fixo (`@modules/fee-fixo/constants/
// tour-fee-fixo.js`): as convenções `alvo`, `alvoAoAbrir`, `aguarda`,
// `avancaEm`, `avancaNoClique`, `abreNoProximo`, `semAlvo` e `semAguarda` estão
// explicadas no cabeçalho de lá e valem aqui sem mudança. O TEXTO de cada
// micro-passo vem do catálogo (`metadata.instructions[]`) e é casado com esta
// lista POR ÍNDICE — uma instrução nova no backend desalinha os passos
// seguintes, e `tour-compliance.unit.js` fica vermelho contra o snapshot.
//
// ★ Este arquivo É o escopo do tour: as 5 etapas da trilha "Aplicar ao
// cliente". Todas levam à MESMA tela, a Visão Geral (`/compliance/visao-geral`)
// — é lá que moram o "Adicionar cliente" e o menu de ações de cada linha, que
// abre os modais de formulário, IPS, KYC/PLD e contratos. As outras etapas do
// módulo (configurar formulário/IPS, modelos, kit…) não têm entrada aqui e
// seguem no drawer de texto.
//
// Prefixo `cp-` nas âncoras: `compliance-*` já é do tour "o que mudou"
// (`constants/whatsnew.js`), e a varredura inversa do teste só enxerga `cp-`.
import { MODAL, RELOAD } from '@constants/Text'

// Slug do módulo no catálogo. Precisa bater com o `slug` do backend —
// divergir faz o lookup devolver null e o tour sumir em silêncio (mesma
// armadilha do `module-key` da barra da Jornada nesta mesma view).
export const MODULE_KEY_COMPLIANCE = 'compliance'

const ADICIONAR_CLIENTE = '[data-tour="cp-adicionar-cliente"]'
const FORM_NOVO_CLIENTE = '[data-tour="cp-novo-cliente-form"]'
const SALVAR_NOVO_CLIENTE = '[data-tour="cp-novo-cliente-salvar"]'
const BUSCA = '[data-tour="cp-busca"]'
const TABELA = '[data-tour="cp-tabela"]'
// Presente em TODA linha da tabela: o host resolve com `document.querySelector`,
// que devolve a primeira no DOM — a primeira linha EXIBIDA, já com busca,
// ordenação e página aplicadas. Ver o comentário no slot `item.actions` da view.
const ACOES_DO_CLIENTE = '[data-tour="cp-acoes-cliente"]'

// Âncora de cada item do menu de ações da linha (campo `dataTour` das ações
// de `getOverviewActions`, renderizado pelo BaseActionsMenu).
export const ACAO_DO_MENU_POR_ETAPA = {
  'enviar-formulario': '[data-tour="cp-acao-formulario"]',
  'enviar-ips': '[data-tour="cp-acao-ips"]',
  'compliance-kyc-pld': '[data-tour="cp-acao-kyc"]',
  'enviar-contrato': '[data-tour="cp-acao-contratos"]',
}

// As 4 etapas "por cliente" têm o MESMO roteiro — muda só o item do menu.
// Uma fábrica em vez de 4 cópias: um ajuste de passo (ex.: trocar o alvo da
// busca) valendo para uma etapa e esquecido nas outras é exatamente a
// divergência que duplicar convida.
const passosDaAcaoNoCliente = (alvoDaAcao) => [
  // "Localize o cliente" — a busca acima da tabela. Só LOCALIZA: clicar no
  // campo para digitar não conclui nada.
  { alvo: BUSCA, avancaNoClique: false },
  // "Abra as ações do cliente" — os três pontos da linha. AVANÇA no clique:
  // abrir o menu é a ação do passo, e o passo seguinte mora dentro dele.
  //
  // `abreNoProximo`: o "Próximo" clica o `v-btn` real (o host procura o
  // primeiro acionável dentro do `span` ancorado). É abertura de menu —
  // idempotente, sem efeito no servidor —, dentro do escopo permitido. O
  // click-outside do Vuetify 2.0.11 descarta evento `!isTrusted`, então o menu
  // aberto por clique programático não fecha sozinho. Sem isto, "Próximo"
  // levava ao passo 3 apontando um item de um menu fechado.
  { alvo: ACOES_DO_CLIENTE, abreNoProximo: true },
  // "Escolha …" — o item do menu. Fechado o menu, o furo fica nos três pontos
  // (onde o usuário precisa agir); aberto, salta para o item. Clicar no item
  // É a ação (abre o modal da etapa), então avança no clique — e por isso
  // NUNCA `abreNoProximo`: escolher a ação é do usuário.
  //
  // Limite conhecido: com o menu FECHADO, o alvo é o botão de três pontos, e
  // clicá-lo neste passo avança para o 4 (o listener está no alvo resolvido).
  // Mesmo trade-off do "Continuar" do Fee Fixo; o `abreNoProximo` do passo
  // anterior faz o caso comum chegar aqui com o menu já aberto.
  {
    alvo: ACOES_DO_CLIENTE,
    alvoAoAbrir: alvoDaAcao,
    semAguarda:
      'o menu de ações da linha é um v-menu, que não emite canal do GlobalBus ao abrir; quem pega a inserção do conteúdo do menu é o MutationObserver do host',
  },
  // "Acompanhe na coluna …" — leitura do status na tabela, não é ação.
  { alvo: TABELA, avancaNoClique: false },
]

export const PASSOS_DO_TOUR = {
  'compliance-cadastrar-cliente': [
    // "Clique em 'Adicionar cliente'" — a ação primária do header. AVANÇA no
    // clique: o passo seguinte acontece dentro do formulário que ele abre.
    //
    // `abreNoProximo`: o botão só emite `MODAL.CADASTRAR_CLIENTES` com
    // `{ direct: true }`, e o modal global abre o formulário limpo
    // (`openNewClientForm` → `resetClientForm`) — idempotente e sem request.
    { alvo: ADICIONAR_CLIENTE, abreNoProximo: true },
    // "Preencha os dados" — o formulário do modal global
    // `src/modal/ModalCadastroClientes.vue`. Fechado, aponta o botão que o
    // abre; aberto, o furo pula para o formulário. Preencher termina quando o
    // usuário diz — no "Próximo".
    {
      alvo: ADICIONAR_CLIENTE,
      alvoAoAbrir: FORM_NOVO_CLIENTE,
      aguarda: MODAL.CADASTRAR_CLIENTES,
      avancaNoClique: false,
    },
    // "Salve o cliente" — o único passo que GRAVA (`POST /novo_cliente`), e
    // por isso NUNCA `abreNoProximo`. Conclui pelo EVENTO, não pelo clique:
    // salvar pode falhar na validação, e só o sucesso emite `RELOAD.CLIENTS`
    // (o mesmo canal que a Visão Geral já escuta para recarregar a tabela).
    {
      alvo: ADICIONAR_CLIENTE,
      alvoAoAbrir: SALVAR_NOVO_CLIENTE,
      aguarda: MODAL.CADASTRAR_CLIENTES,
      avancaEm: RELOAD.CLIENTS,
      avancaNoClique: false,
    },
    // "Confira na tabela" — leitura, não é ação.
    { alvo: TABELA, avancaNoClique: false },
  ],

  'enviar-formulario': passosDaAcaoNoCliente(
    ACAO_DO_MENU_POR_ETAPA['enviar-formulario']
  ),
  'enviar-ips': passosDaAcaoNoCliente(ACAO_DO_MENU_POR_ETAPA['enviar-ips']),
  'compliance-kyc-pld': passosDaAcaoNoCliente(
    ACAO_DO_MENU_POR_ETAPA['compliance-kyc-pld']
  ),
  'enviar-contrato': passosDaAcaoNoCliente(
    ACAO_DO_MENU_POR_ETAPA['enviar-contrato']
  ),
}

export function temTourGuiado(slug) {
  if (!slug) return false
  return Object.hasOwn(PASSOS_DO_TOUR, slug)
}
